import { CommonModule } from '@angular/common';
import { Component, computed, inject, signal } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { Router } from '@angular/router';
import { PayoutMethod } from '@app/modules/direct/services/payout-methods.service';
import { UsersFacade } from '@app/shared/+state/users';
import { CleansterPropertyData, MarketplaceTeammate, TaskChecklist } from '@app/shared/interfaces/lib/task.interface';
import { PropertiesService, PropertyIncludes } from '@app/shared/services/properties/properties.service';
import { TasksService } from '@app/shared/services/tasks/tasks.service';
import { ToastNotificationsService } from '@app/shared/services/toast-notifications/toast-notifications.service';
import { Property, PropertyRadioGroupComponent } from '@core';
import { ButtonComponent, Checklist, LoadingSpinnerComponent, ModalAction, ModalFullComponent } from '@ui';
import { Logger } from '@utils';
import { Subject, of } from 'rxjs';
import { catchError, debounceTime, map, switchMap } from 'rxjs/operators';
import { AddTaskRuleFlowAccessComponent } from './add-task-rule-flow-access/add-task-rule-flow-access.component';
import { AddTaskRuleFlowChecklistComponent } from './add-task-rule-flow-checklist/add-task-rule-flow-checklist.component';
import { AddTaskRuleFlowCleanersComponent } from './add-task-rule-flow-cleaners/add-task-rule-flow-cleaners.component';
import { AddTaskRuleFlowCleaningAutoManualComponent } from './add-task-rule-flow-cleaning-auto-manual/add-task-rule-flow-cleaning-auto-manual.component';
import { AddTaskRuleFlowCleaningDetailsComponent } from './add-task-rule-flow-cleaning-details/add-task-rule-flow-cleaning-details.component';
import { AddTaskRuleFlowConfirmComponent } from './add-task-rule-flow-confirm/add-task-rule-flow-confirm.component';
import { AddTaskRuleFlowCostAndPaymentsComponent } from './add-task-rule-flow-cost-and-payments/add-task-rule-flow-cost-and-payments.component';
import { AddTaskRuleFlowManualTaskWhenComponent } from './add-task-rule-flow-manual-task-when/add-task-rule-flow-manual-task-when.component';
import { AddTaskRuleFlowPropertyDisplayComponent } from './add-task-rule-flow-property-display/add-task-rule-flow-property-display.component';
import { AddTaskRuleFlowTaskTypeComponent } from './add-task-rule-flow-task-type/add-task-rule-flow-task-type.component';

type Step =
  | 'task-type'
  | 'manual-task-when'
  | 'cleaning-auto-manual'
  | 'auto-property'
  | 'auto-info'
  | 'auto-cleaning-details'
  | 'auto-checklist'
  | 'auto-cleaners'
  | 'auto-access'
  | 'auto-cost-and-payments'
  | 'auto-confirm';

@Component({
  selector: 'sbnb-add-task-rule-flow',
  templateUrl: './add-task-rule-flow.component.html',
  standalone: true,
  imports: [
    CommonModule,
    ModalFullComponent,
    ButtonComponent,
    AddTaskRuleFlowTaskTypeComponent,
    AddTaskRuleFlowManualTaskWhenComponent,
    AddTaskRuleFlowCleaningAutoManualComponent,
    AddTaskRuleFlowPropertyDisplayComponent,
    AddTaskRuleFlowCleaningDetailsComponent,
    AddTaskRuleFlowChecklistComponent,
    AddTaskRuleFlowCleanersComponent,
    AddTaskRuleFlowAccessComponent,
    AddTaskRuleFlowCostAndPaymentsComponent,
    AddTaskRuleFlowConfirmComponent,
    PropertyRadioGroupComponent,
    LoadingSpinnerComponent,
  ],
})
export class AddTaskRuleFlowComponent {
  readonly #router = inject(Router);
  readonly #taskService = inject(TasksService);
  readonly #propertiesService = inject(PropertiesService);
  readonly #toastService = inject(ToastNotificationsService);
  readonly #usersFacade = inject(UsersFacade);

  activeStep = signal<Step>('task-type');

  payload = signal<{
    task_type: string | null;
    when: string | null;
    cleaning_option: 'automatic' | 'manual' | null;
    property: Property | null;
    checklist: Checklist | null;
    create_operation_codes: boolean | null;
    note: string | null;
    cleaning_hours: number | null;
    provide_cleaning_supplies: boolean | null;
  }>({
    task_type: null,
    when: null,
    cleaning_option: null,
    property: null,
    checklist: {
      groups: [
        {
          title: '',
          items: [],
        },
      ],
    },
    create_operation_codes: null,
    note: null,
    cleaning_hours: null,
    provide_cleaning_supplies: null,
  });

  propertyCleansterData = signal<CleansterPropertyData | null>(null);
  cleaners = signal<MarketplaceTeammate[]>([]);
  cleanersLoading = signal<boolean>(false);

  progress = computed(() => {
    const stepProgress: Record<Step, number> = {
      'task-type': 10,
      'manual-task-when': 20,
      'cleaning-auto-manual': 20,
      'auto-property': 30,
      'auto-info': 40,
      'auto-cleaning-details': 50,
      'auto-checklist': 60,
      'auto-cleaners': 70,
      'auto-access': 80,
      'auto-cost-and-payments': 95,
      'auto-confirm': 100,
    };

    return stepProgress[this.activeStep()] || 10;
  });

  primaryActionLoading = signal<boolean>(false);

  primaryAction = computed<ModalAction>(() => {
    const step = this.activeStep();

    if (step === 'auto-confirm') {
      return {
        label: 'Activate autopilot cleaning',
        loading: this.primaryActionLoading(),
        disabled: false,
        onClick: () => this.#createAutomatedCleaningTaskRule(),
      };
    }

    return {
      label: 'Continue',
      loading: this.primaryActionLoading(),
      disabled: false,
      onClick: () => this.nextStep(),
    };
  });

  secondaryAction = signal<ModalAction>({
    label: 'Back',
    disabled: false,
    loading: false,
    onClick: () => this.handleBack(),
  });

  marketplacePropertyFilter = this.#propertiesService.marketplacePropertyFilter();
  potentialProperties = signal<Property[]>([]);

  isOnLegacyPlan = signal<boolean>(false);

  aiSuggestionForChecklistRequested = signal<TaskChecklist | null>(null);

  #estimateRequest$ = new Subject<{ duration_hours: number; provide_cleaning_supplies: boolean }>();
  estimateLoading = signal<boolean>(false);

  paymentMethodDetailsLoading = signal<boolean>(false);
  paymentMethodDetails = signal<PayoutMethod | null>(null);

  constructor() {
    // Subscribe to legacy plan status
    this.#usersFacade.isOnLegacyPlan$.pipe(takeUntilDestroyed()).subscribe((isLegacy) => {
      this.isOnLegacyPlan.set(isLegacy);
    });

    // Set up debounced estimate requests
    this.#estimateRequest$
      .pipe(
        debounceTime(300),
        switchMap((params) => {
          const propertyId = this.payload().property?.id;
          if (!propertyId) {
            return [];
          }

          this.estimateLoading.set(true);
          return this.#taskService.fetchPropertyCleansterEstimate(propertyId, params).pipe(
            map((data) => ({ data, error: null })),
            catchError((error) => {
              Logger.error('Error fetching estimate:', error);
              this.estimateLoading.set(false);
              return of({ data: null, error });
            })
          );
        }),
        takeUntilDestroyed()
      )
      .subscribe({
        next: (result: { data: CleansterPropertyData | null; error: unknown }) => {
          this.estimateLoading.set(false);
          if (result.data) {
            this.propertyCleansterData.update((current) => ({
              ...current,
              estimated_cost_display: result.data.estimated_cost_display,
            }));
          }
        },
      });

    this.#propertiesService
      .getProperties({
        filterCriteria: this.marketplacePropertyFilter,
        paginate: false,
        includes: [PropertyIncludes.HasSmartlocks],
      })
      .pipe(map((res) => res.data))
      .subscribe({
        next: (properties) => {
          Logger.log('AddTaskRuleFlowComponent: Received properties:', properties);

          if (properties?.length === 0) {
            Logger.error('AddTaskRuleFlowComponent: Error fetching properties: Properties is empty');
            return;
          }

          this.potentialProperties.set(properties);
        },
        error: (error) => {
          Logger.error('AddTaskRuleFlowComponent: Error fetching properties:', error);
        },
      });
  }

  onTaskTypeSelected(taskType: string): void {
    this.payload.update((current) => ({
      ...current,
      task_type: taskType,
    }));
  }

  onWhenSelected(when: string): void {
    this.payload.update((current) => ({
      ...current,
      when: when,
    }));
  }

  onCleaningOptionSelected(option: 'automatic' | 'manual'): void {
    this.payload.update((current) => ({
      ...current,
      cleaning_option: option,
    }));
  }

  onPropertySelected(property: Property) {
    this.payload.update((current) => ({
      ...current,
      property,
    }));
  }

  onCreateOperationCodesChanged(value: boolean) {
    this.payload.update((current) => ({
      ...current,
      create_operation_codes: value,
    }));
  }

  onNoteChanged(value: string) {
    this.payload.update((current) => ({
      ...current,
      note: value,
    }));
  }

  onCleaningHoursChanged(cleaningHours: number) {
    this.payload.update((current) => ({
      ...current,
      cleaning_hours: cleaningHours,
    }));

    this.#fetchFinancialQuote();
  }

  onProvideCleaningSuppliesChanged(provideCleaningSupplies: boolean) {
    this.payload.update((current) => ({
      ...current,
      provide_cleaning_supplies: provideCleaningSupplies,
    }));

    this.#fetchFinancialQuote();
  }

  createCheckInTaskRule() {
    this.primaryActionLoading.set(true);
    this.#taskService.createTaskRule(2, -3600, 'checkin').subscribe(
      (res) => {
        if (res.data?.uuid) {
          this.#router.navigate(['/operations/task-rules/task/' + res.data.uuid]);
        }
        this.primaryActionLoading.set(false);
      },
      () => {
        this.primaryActionLoading.set(false);
      }
    );
  }

  createCleaningManualTaskRule() {
    this.primaryActionLoading.set(true);
    this.#taskService.createTaskRule(1, 3600, 'checkout').subscribe(
      (res) => {
        if (res.data?.uuid) {
          this.#router.navigate(['/operations/task-rules/task/' + res.data.uuid]);
        }
        this.primaryActionLoading.set(false);
      },
      () => {
        this.primaryActionLoading.set(false);
      }
    );
  }

  createManualTaskRule() {
    const taskTypeMap: Record<string, number> = {
      checkout: 4,
      maintenance: 5,
    };

    const whenMap: Record<string, number> = {
      'before-checkout': -3600,
      'after-checkout': 3600,
      recurring: -3600,
      'before-checkin': -3600,
      'after-checkin': 3600,
    };

    const relativeToMap: Record<string, string> = {
      'before-checkout': 'checkout',
      'after-checkout': 'checkout',
      recurring: 'recurring',
      'before-checkin': 'checkin',
      'after-checkin': 'checkin',
    };

    const taskType = taskTypeMap[this.payload().task_type!];
    const when = whenMap[this.payload().when!];
    const relative_to = relativeToMap[this.payload().when!];

    this.primaryActionLoading.set(true);
    this.#taskService.createTaskRule(taskType, when, relative_to).subscribe(
      (res) => {
        if (res.data?.uuid) {
          this.#router.navigate(['/operations/task-rules/task/' + res.data.uuid]);
        }
        this.primaryActionLoading.set(false);
      },
      () => {
        this.primaryActionLoading.set(false);
      }
    );
  }

  #createAutomatedCleaningTaskRule() {
    const payload = this.payload();

    // Map the payload to the format expected by the service
    const servicePayload = {
      propertyId: payload.property!.id,
      propertyName: payload.property!.name,
      checklist: payload.checklist,
      create_operation_codes: payload.create_operation_codes,
      note: payload.note,
      duration_hours: payload.cleaning_hours,
      provide_cleaning_supplies: payload.provide_cleaning_supplies,
    };

    // Set loading state
    this.primaryActionLoading.set(true);

    // Call the service
    this.#taskService.createMarketplaceTaskRuleFromOnboardingFlow(servicePayload).subscribe({
      next: (taskRule) => {
        this.primaryActionLoading.set(false);
        // Redirect to the task rule detail page
        if (taskRule.uuid) {
          this.#router.navigate(['/operations/task-rules/task/' + taskRule.uuid]);
        }
      },
      error: (error) => {
        this.primaryActionLoading.set(false);
        // Display error from API
        if (error?.error?.message) {
          this.#toastService.error(error.error.message);
        } else {
          this.#toastService.error('Failed to create automated cleaning task rule. Please try again.');
        }
      },
    });
  }

  nextStep() {
    const currentStep = this.activeStep();
    const payload = this.payload();

    switch (currentStep) {
      case 'task-type': {
        if (!payload.task_type) {
          this.#toastService.error('Please select a task type before continuing.');
          return;
        }

        if (payload.task_type === 'cleaning') {
          this.activeStep.set('cleaning-auto-manual');
        } else if (payload.task_type === 'check-in') {
          // For check-in, create the task rule directly
          this.createCheckInTaskRule();
        } else if (payload.task_type === 'checkout' || payload.task_type === 'maintenance') {
          // For checkout and maintenance, go to manual-task-when step
          this.activeStep.set('manual-task-when');
        }
        break;
      }

      case 'manual-task-when': {
        if (!payload.when) {
          this.#toastService.error('Please select when the task should be created.');
          return;
        }
        this.createManualTaskRule();
        break;
      }

      case 'cleaning-auto-manual': {
        if (!payload.cleaning_option) {
          this.#toastService.error('Please select a cleaning option before continuing.');
          return;
        }

        if (payload.cleaning_option === 'automatic') {
          this.activeStep.set('auto-property');
        } else {
          this.createCleaningManualTaskRule();
        }
        break;
      }

      case 'auto-property': {
        if (!payload.property) {
          this.#toastService.error('Please select a property before continuing.');
          return;
        }

        // We need to fetch some details that we'll need for the rest of the flow, now that we know the property
        this.#fetchPropertyCleaningData();
        // We also want to proactively fetch a checklist for the property - since it takes can take a while to generate
        this.#fetchAiSuggestionForChecklist();

        this.activeStep.set('auto-info');
        break;
      }

      case 'auto-info': {
        this.activeStep.set('auto-cleaning-details');
        break;
      }

      case 'auto-cleaning-details': {
        // No validation needed for details step
        this.activeStep.set('auto-checklist');
        break;
      }

      case 'auto-checklist': {
        if (!payload.checklist || payload.checklist.groups.every((group) => group.items.length === 0)) {
          this.#toastService.error('Please add at least one item to the checklist.');
          return;
        }

        // Fetch cleaners
        this.#fetchCleaners();

        this.activeStep.set('auto-cleaners');
        break;
      }

      case 'auto-cleaners': {
        // No validation needed for cleaners step
        this.activeStep.set('auto-access');
        break;
      }

      case 'auto-access': {
        if (payload.create_operation_codes === null) {
          this.#toastService.error('Please select whether to create smart lock codes for this task.');
          return;
        }
        if (payload.create_operation_codes === false && !payload.note?.trim()) {
          this.#toastService.error('Please provide access instructions when not creating smart lock codes.');
          return;
        }

        // Fetch payment method details
        this.#fetchPaymentMethodDetails();

        this.activeStep.set('auto-cost-and-payments');
        break;
      }

      case 'auto-cost-and-payments': {
        if (!this.paymentMethodDetails()) {
          this.#toastService.error('Please add a payment method before continuing.');
          return;
        }

        this.activeStep.set('auto-confirm');
        break;
      }
    }
  }

  handleBack() {
    const currentStep = this.activeStep();

    switch (currentStep) {
      case 'task-type':
        this.close();
        break;

      case 'manual-task-when':
        this.activeStep.set('task-type');
        break;

      case 'cleaning-auto-manual':
        this.activeStep.set('task-type');
        break;

      case 'auto-property':
        this.activeStep.set('cleaning-auto-manual');
        break;

      case 'auto-info':
        this.activeStep.set('auto-property');
        break;

      case 'auto-cleaning-details':
        this.activeStep.set('auto-info');
        break;

      case 'auto-checklist':
        this.activeStep.set('auto-cleaning-details');
        break;

      case 'auto-cleaners':
        this.activeStep.set('auto-checklist');
        break;

      case 'auto-access':
        this.activeStep.set('auto-cleaners');
        break;

      case 'auto-cost-and-payments':
        this.activeStep.set('auto-access');
        break;

      case 'auto-confirm':
        this.activeStep.set('auto-cost-and-payments');
        break;
    }
  }

  close() {
    this.#router.navigate(['/operations/task-rules']);
  }

  #fetchPropertyCleaningData() {
    this.#taskService.fetchPropertyCleansterData(this.payload().property!.id).subscribe((res) => {
      this.propertyCleansterData.set(res);

      // Set payload values based on API response
      this.payload.update((current) => ({
        ...current,
        cleaning_hours: res.recommended_cleaning_time,
        provide_cleaning_supplies: res.includes_supplies,
      }));
    });
  }

  #fetchAiSuggestionForChecklist() {
    if (this.aiSuggestionForChecklistRequested()) return;

    this.#taskService.generateChecklistForProperty(this.payload().property!.id).subscribe(
      (res) => {
        this.aiSuggestionForChecklistRequested.set(res);
        this.payload.update((current) => ({
          ...current,
          checklist: res,
        }));
      },
      () => {
        // If it fails, no dramas we'll just continue on
        this.aiSuggestionForChecklistRequested.set({ groups: [] });
      }
    );
  }

  #fetchFinancialQuote() {
    const cleaningHours = this.payload().cleaning_hours;
    const provideSupplies = this.payload().provide_cleaning_supplies;

    if (cleaningHours && provideSupplies !== null) {
      this.#estimateRequest$.next({
        duration_hours: cleaningHours,
        provide_cleaning_supplies: provideSupplies,
      });
    }
  }

  #fetchCleaners() {
    const propertyId = this.payload().property?.id;
    if (!propertyId) {
      Logger.error('Cannot fetch cleaners without a property selected');
      return;
    }

    this.cleanersLoading.set(true);
    this.#taskService.fetchPropertyCleaners(propertyId).subscribe({
      next: (cleaners) => {
        Logger.log('Fetched cleaners:', cleaners);
        this.cleaners.set(cleaners);
        this.cleanersLoading.set(false);
      },
      error: (error) => {
        Logger.error('Error fetching cleaners:', error);
        // Set empty array on error so the flow can continue
        this.cleaners.set([]);
        this.cleanersLoading.set(false);
      },
    });
  }

  #fetchPaymentMethodDetails() {
    this.paymentMethodDetailsLoading.set(true);
    this.#taskService.fetchActiveMarketplacePaymentMethodDetails().subscribe({
      next: (res) => {
        this.paymentMethodDetails.set(res);
        this.paymentMethodDetailsLoading.set(false);
      },
      error: (error) => {
        Logger.error('Error fetching payment method details:', error);
        this.paymentMethodDetailsLoading.set(false);
      },
    });
  }
}
